import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { replace } from 'connected-react-router';
import { isBrowser, isDesktop } from 'react-device-detect';

import Strings from '../../../Strings';
import { actions } from './redux/actions';
import { actions as scheduleActions } from '../Schedules/redux/actions';
import { GET_CAP_PLOT_DATA_RESULT, GET_CHART_SCHEDULE_RESULT } from '../Schedules/redux/constants';
import { PageHeader } from '../../../components/PageHeader';
import Select from '../../../components/Select';
import CapTakesPlot from '../../ShipperCapsDetails/CapTakesPlot';
import { getCapChargeText, signalRender } from '../../../utils';
import MedicationStats from './Dashboard/MedicationStats';
import { PatientDashboardAction } from '../../../actions/patient-dashboard';

function AdherenceGraph({ includeSchedulesWithNoCap = false, patientVersion = false, hideMedSelect = false, ...props }) {
  const { patientId, plotResizeTimestamp, medSelectedFromDashboard, patientMedications, patientCapDetails } = props;
  const { getMedications, loadDashboard, getCapDetails, getSchedule, onScheduleDataLoaded, getCapPlotData } = props;

  const [medications, setMedications] = useState();
  const [plotScheduleData, setPlotScheduleData] = useState();
  const [plotData, setPlotData] = useState();
  const [plotDateRange, setPlotDateRange] = useState({});
  const [selectedMedRootId, setSelectedMedRootId] = useState(medSelectedFromDashboard);
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    if (!selectedMedRootId) {
      getMedications(patientId);
    }
    if (patientVersion) {
      loadDashboard(patientId).then(r => {
        setReminders(r.response?.reminders);
      });
    }
  }, [patientId]);

  useEffect(() => {
    if (medSelectedFromDashboard && medSelectedFromDashboard !== selectedMedRootId) {
      setSelectedMedRootId(medSelectedFromDashboard);
    }
  }, [medSelectedFromDashboard]);

  useEffect(() => {
    if (_.isEqual(patientMedications, medications)) {
      return;
    }

    setMedications(patientMedications);
    if (!patientMedications || patientMedications.length === 0) {
      setSelectedMedRootId(null);
      return;
    }

    // If selected med is in the list, keep it
    if (selectedMedRootId && patientMedications.some(m => m.rootId === selectedMedRootId)) {
      return;
    }
    // Find the first available medication based on preferences
    const medWithCap = patientMedications.find(m => m.capId);
    const fallbackMedId = includeSchedulesWithNoCap ? patientMedications[0].rootId : medWithCap?.rootId;
    if (fallbackMedId) {
      setSelectedMedRootId(fallbackMedId);
    } else {
      setSelectedMedRootId(null);
    }
  }, [patientMedications]);

  useEffect(() => {
    if (!selectedMedRootId || !medications?.length) {
      return;
    }
    getScheduleDetails();
  }, [selectedMedRootId, medications]);

  const getScheduleDetails = () => {
    setPlotScheduleData(null);
    setPlotData(null);

    getSchedule( patientVersion ? null : patientId, selectedMedRootId).then(r => {
      if (r.type === GET_CHART_SCHEDULE_RESULT) {
        const scheduleData = r.response;
        if (scheduleData && scheduleData.schedules) {
          scheduleData.schedules = scheduleData.schedules.filter(s => s.hours);
        }
        setPlotScheduleData(scheduleData);
        if (onScheduleDataLoaded) {
          onScheduleDataLoaded(scheduleData);
        }
        const end = r.response.end_date ? moment(r.response.end_date * 1000) : moment().endOf('day');
        const start = moment(r.response.start_date * 1000);
        const dateRange = {
          start,
          end: end < start ? start.clone().add(1, 'month') : end > moment().endOf('day') ? moment().endOf('day') : end,
        };
        setPlotDateRange(dateRange);
        loadPlot(dateRange);

        if (scheduleData) {
          const request = {
            offset: 0,
            search: '',
            limit: 1,
            activityType: 'all',
          };

          if (scheduleData.cap_id && !patientVersion) {
            getCapDetails(scheduleData.cap_id, request);
          }
        }
      }
      return r;
    });
  };

  const loadPlot = selectedDateRange => {
    if (!selectedMedRootId || !selectedDateRange) {
      return;
    }

    const request = {
      startDate: selectedDateRange.start.format(),
      endDate: selectedDateRange.end.format(),
    };

    setPlotData(null);

    getCapPlotData(patientVersion ? null : patientId, selectedMedRootId, request).then(r => {
      if (r.type === GET_CAP_PLOT_DATA_RESULT) {
        setPlotData(r.response);
      }
    });
  };

  const onMedChosen = option => {
    if (option.value !== selectedMedRootId) {
      setPlotData();
      setSelectedMedRootId(option.value);
    }
  };

  return medications?.length > 0 && (medications.some(m => m.capId) || includeSchedulesWithNoCap) ? (
    <div className="medication-details-page" style={{ paddingTop: 0 }}>
      {!patientVersion && isDesktop && (
        <PageHeader
          right={medications && medications.length > 0 && !hideMedSelect && (
            <Select
              data={medications
                .filter(med => med.capId || includeSchedulesWithNoCap)
                .map(m => ({ value: m.rootId, label: m.medicationName.substring(0, 50) }))}
              onChange={onMedChosen}
              value={selectedMedRootId}
            />
          )}
          left={(
            <div className="legendContainer">
              {plotScheduleData?.cap_id && (
                <>
                  <div className="label greyLabel">{`${Strings.capId}: ${plotScheduleData?.cap_id}`}</div>
                  <div className="hr20"></div>
                  <div className="label greyLabel">
                    {Strings.battery.battery}: {getCapChargeText(patientCapDetails?.battery)}
                  </div>
                  <div className="hr20"></div>
                  {patientCapDetails?.version !== 'ble' && (
                    <>
                      <div className="label greyLabel">
                        {Strings.signal}: {signalRender(patientCapDetails?.signal_strength)}
                      </div>
                      <div className="hr20"></div>
                    </>
                  )}
                </>
              )}
              {plotScheduleData?.schedules?.some(s => s.hours) && (
                <>
                  <div className="label greyLabel">{`${Strings.avgTimeDelta}: ${plotData?.avg_time_delta >= 0 ? '+' : '-'}${plotData?.avg_time_delta || plotData?.avg_time_delta === 0
                      ? `${plotData?.avg_time_delta} ${Strings.min}`
                      : '-'
                    }`}</div>
                  <div className="hr20"></div>
                  <div className="label greyLabel">{`${Strings.adherenceForPeriod}: ${plotData?.adherence || plotData?.adherence === 0 ? `${plotData?.adherence}%` : '-'
                    }`}</div>
                </>
              )}
            </div>
          )}
        />
      )}
      {patientVersion && isBrowser && (
        <PageHeader left={medications.find(m => m.id === selectedMedRootId)?.medicationName} />
      )}
      {plotData && patientVersion && (
        <div className="mini-adherence-dashboard">
          <MedicationStats
            data={plotData}
            nextTake={reminders.find(r => r.id === selectedMedRootId)}
            medication={medications.find(m => m.rootId === selectedMedRootId)}
          />
        </div>
      )}
      {plotData ? (
        <div className="capEventsContainer">
          <CapTakesPlot
            plotData={plotData}
            plotScheduleData={plotScheduleData}
            graphMinDate={plotDateRange.start}
            graphMaxDate={plotDateRange.end}
            plotResizeTimestamp={plotResizeTimestamp}
          />
        </div>
      ) : (
        <div className="boxStatsWithGraph-graph noStats">
          <div className="empty-row-container">
            <div className="empty-row-inner">{Strings.noDataAvailable}</div>
          </div>
        </div>
      )}
    </div>
  ) : (
    null
  );
}

AdherenceGraph.propTypes = {
  getMedications: PropTypes.func,
  patientMedications: PropTypes.array,
  onAddMedicine: PropTypes.func,
  patientId: PropTypes.any,
  getSchedule: PropTypes.func,
  timezone: PropTypes.string,
  getCapPlotData: PropTypes.func,
  patientCapDetails: PropTypes.any,
  getCapDetails: PropTypes.func,
  loadDashboard: PropTypes.func,
  getDevices: PropTypes.func,
  includeSchedulesWithNoCap: PropTypes.bool,
  patientVersion: PropTypes.bool,
  hideMedSelect: PropTypes.bool,
  medSelectedFromDashboard: PropTypes.any,
  plotResizeTimestamp: PropTypes.any,
  onScheduleDataLoaded: PropTypes.func,
};

const mapStateToProps = state => {
  const urlParams = new URLSearchParams(window.location.search);
  const { patients } = state.superUser;
  return {
    patientMedications: patients?.patientMedications?.medications,
    timezone: state.auth?.profile?.preferences?.timezone,
    selectedMedId: parseInt(urlParams.get('medId'), 10),
    patientCapDetails: patients?.patientCapDetails,
  };
};

const mapDispatchToProps = dispatch => ({
  getMedications: patientId => dispatch(actions.getMedications(patientId)),
  getSchedule: (patientId, scheduleRootId) =>
    patientId ? dispatch(scheduleActions.getChartSchedule(patientId, scheduleRootId)) : dispatch(scheduleActions.getSelfChartSchedule(scheduleRootId)),
  getCapPlotData: (patientId, scheduleId, request) =>
    patientId ? dispatch(scheduleActions.getCapPlotData(patientId, scheduleId, request)) : dispatch(scheduleActions.getSelfCapPlotData(scheduleId, request)),
  getCapDetails: (id, pageRequest) => dispatch(actions.getCapDetails(id, pageRequest)),
  replacePath: path => dispatch(replace(path)),
  loadDashboard: id => dispatch(PatientDashboardAction.getDashboard(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdherenceGraph);
